import React from "react"
import PropTypes from "prop-types"
import { Alert, Col, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import moment from "moment"

import { Button, ControllerHF, ComboboxFormField, DateInputField } from "components"
import {
  BILLING_SUBSCRIPTION_TARIFF_ALIAS_BUSINESS,
  BILLING_SUBSCRIPTION_TARIFF_ALIAS_OPTIONS,
} from "consts"

export const UserSubscriptionForm = props => {
  const { t } = useTranslation()
  const { user } = props

  const subscription = user?.subscription
  const hasStripeSubscription =
    !!subscription && subscription.is_manual === false
  const hasManualSubscription =
    !!subscription && subscription.is_manual === true

  const tariffOptions = BILLING_SUBSCRIPTION_TARIFF_ALIAS_OPTIONS.map(
    option => ({ value: option.value, label: t(option.label) })
  )

  const { control, handleSubmit, setError, reset } = useForm({
    defaultValues: {
      tariff_alias:
        subscription?.tariff?.alias || BILLING_SUBSCRIPTION_TARIFF_ALIAS_BUSINESS,
      ends_at: subscription?.ends_at
        ? moment(subscription.ends_at).toDate()
        : null,
    },
    resolver: yupResolver(
      Yup.object().shape({
        tariff_alias: Yup.string()
          .nullable()
          .required("field.error.required"),
        ends_at: Yup.date()
          .nullable()
          .min(moment().startOf("day").toDate(), "field.error.date_in_past"),
      })
    ),
  })

  const onSubmit = values => {
    const params = {
      tariff_alias: values.tariff_alias,
      ...(values.ends_at
        ? { ends_at: moment(values.ends_at).format("YYYY-MM-DD") }
        : {}),
    }

    props.onSubmit(params, { reset, setError })
  }

  if (hasStripeSubscription) {
    return (
      <>
        <Alert color="warning">{t("subscription.stripe_managed")}</Alert>
        <div className="mt-3">
          <div className="button-items">
            <Button
              title={"close"}
              color="light"
              outline
              onClick={props.onCancel}
            />
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {props.error && <Alert color="danger">{props.error.message}</Alert>}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md={6}>
            <ControllerHF
              name={"tariff_alias"}
              control={control}
              component={ComboboxFormField}
              id={"tariff_alias"}
              label={t("field.label.subscription_tariff")}
              placeholder={t("field.placeholder.subscription_tariff")}
              options={tariffOptions}
              isDetermineValue
              normalize={option => (option ? option.value : null)}
            />
          </Col>

          <Col md={6}>
            <ControllerHF
              name={"ends_at"}
              control={control}
              component={DateInputField}
              id={"ends_at"}
              label={t("field.label.subscription_ends_at")}
              placeholder={t("field.placeholder.subscription_ends_at")}
            />
          </Col>
        </Row>

        <div className="mt-3 d-flex justify-content-between">
          <div className="button-items">
            <Button
              submit
              title={"btn.save"}
              disabled={props.loading || props.deleting}
              loading={props.loading}
            />

            <Button
              title={"cancel"}
              color="light"
              outline
              onClick={props.onCancel}
            />
          </div>

          {hasManualSubscription && (
            <Button
              title={"subscription.remove"}
              color="danger"
              outline
              disabled={props.loading || props.deleting}
              loading={props.deleting}
              onClick={props.onDelete}
            />
          )}
        </div>
      </form>
    </>
  )
}

UserSubscriptionForm.propTypes = {
  user: PropTypes.object,
  loading: PropTypes.bool,
  deleting: PropTypes.bool,
  error: PropTypes.object,
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func,
  onCancel: PropTypes.func,
}
